import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteViewMutation, useSaveViewMutation } from '@platform/state/api';
import { IconButton } from '@platform/ui';
import type { IViewSettingsDrawerProps } from './IViewSettingsDrawerProps';
import { useDrawerKeys } from './useDrawerKeys';
import { ViewBoardColumns } from './ViewBoardColumns';
import { ViewCardFields } from './ViewCardFields';
import { ViewColourRules } from './ViewColourRules';
import { ViewColumnsEditor } from './ViewColumnsEditor';
import { ViewFiltersEditor } from './ViewFiltersEditor';
import { ViewKindPicker } from './ViewKindPicker';
import { useCanQueryPii } from '../../fields/useCanQueryPii';
import { ViewSortEditor } from './ViewSortEditor';
import { writeOf } from './viewDraft';
import './drawer.css';

/**
 * «Налаштування подання» (UIUX tables/saved-views, прототип v4): панель праворуч поверх затемнення (клік по ньому чи Esc — скасувати):
 * назва, тип подання, стовпці (для таблиці й «усіх подань»), стовпці дошки (дошка й «усі»), поля й кольори карток (крім таблиці), фільтри,
 * сортування, розмір сторінки (крім дошки); внизу «Зберегти подання»,
 * для наявного — «Зберегти як нове» й «Видалити» (з підтвердженням у тому ж рядку), «Скасувати». Збереження — API подань.
 */
export function ViewSettingsDrawer({ ws, rt, view, initial, onClose, onSaved, onDeleted }: IViewSettingsDrawerProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(initial);
  const [confirming, setConfirming] = useState(false);
  const [save, { isLoading: saving }] = useSaveViewMutation();
  const [remove, { isLoading: deleting }] = useDeleteViewMutation();
  const box = useRef<HTMLElement>(null);
  const pii = useCanQueryPii(ws);
  useDrawerKeys(box, onClose);
  const patch = (p: Partial<typeof draft>) => setDraft((d) => ({ ...d, ...p }));
  const submit = (asNew: boolean) => {
    const body = writeOf(asNew ? { ...draft, name: `${draft.name}${t('viewSettings.copySuffix')}` } : draft, rt.key, t('viewSettings.untitled'));
    void save({ ws, body, viewId: asNew ? undefined : view?.id }).unwrap().then(onSaved).catch(() => undefined);
  };
  const drop = () => { if (view) void remove({ ws, viewId: view.id }).unwrap().then(onDeleted).catch(() => undefined); };
  const busy = saving || deleting;
  return (
    <div className="lv-vs__overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside ref={box} className="lv-vs" role="dialog" aria-modal="true" aria-label={t('viewSettings.title')} tabIndex={-1}>
        <div className="lv-vs__head">
          <div className="lv-vs__titles">
            <strong className="lv-vs__title">{t('viewSettings.title')}</strong>
            <span className="lv-vs__sub">{t(`viewSettings.sub.${draft.kind}`)}</span>
          </div>
          <IconButton icon="x" label={t('viewSettings.close')} round onClick={onClose} />
        </div>
        <div className="lv-vs__body">
          <label className="lv-vs__field">
            <span className="lv-vs__label">{t('viewSettings.name')}</span>
            <input className="lv-vs__name" value={draft.name} autoFocus onChange={(e) => patch({ name: e.target.value })} />
          </label>
          <ViewKindPicker kind={draft.kind} onChange={(kind) => patch({ kind })} />
          {(draft.kind === 'any' || draft.kind === 'table') && <ViewColumnsEditor rt={rt} columns={draft.columns} onChange={(columns) => patch({ columns })} />}
          {(draft.kind === 'any' || draft.kind === 'board') && <ViewBoardColumns rt={rt} groupBy={draft.groupBy} onChange={(groupBy) => patch({ groupBy })} />}
          {draft.kind !== 'table' && <ViewCardFields rt={rt} fields={draft.cardFields} onChange={(cardFields) => patch({ cardFields })} />}
          {draft.kind !== 'table' && <ViewColourRules rt={rt} rules={draft.rules} onChange={(rules) => patch({ rules })} />}
          <ViewFiltersEditor ws={ws} rt={rt} filters={draft.filters} onChange={(filters) => patch({ filters })} />
          <ViewSortEditor rt={rt} sort={draft.sort} pageSize={draft.pageSize} paging={draft.kind !== 'board'} onSort={(sort) => patch({ sort })} onPageSize={(pageSize) => patch({ pageSize })}
            pii={pii} />
        </div>
        <div className="lv-vs__foot">
          {confirming ? (
            <>
              <span className="lv-vs__confirm">{t('viewSettings.deleteConfirm', { name: view?.name ?? '' })}</span>
              <span className="lv-vs__spacer" />
              <button type="button" className="lv-head-btn" onClick={() => setConfirming(false)}>{t('viewSettings.cancel')}</button>
              <button type="button" className="lv-head-btn lv-head-btn--danger" disabled={busy} onClick={drop}>{t('viewSettings.delete')}</button>
            </>
          ) : (
            <>
              <button type="button" className="lv-head-btn lv-head-btn--primary" disabled={busy} onClick={() => submit(false)}>{t('viewSettings.save')}</button>
              {view && <button type="button" className="lv-head-btn" disabled={busy} onClick={() => submit(true)}>{t('viewSettings.saveAsNew')}</button>}
              {view && <button type="button" className="lv-head-btn lv-head-btn--danger" onClick={() => setConfirming(true)}>{t('viewSettings.delete')}</button>}
              <span className="lv-vs__spacer" />
              <button type="button" className="lv-head-btn" onClick={onClose}>{t('viewSettings.cancel')}</button>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
